/**
 * Shared validation and sanitization utilities for password, email, and input handling.
 * Mirrors backend/src/utils/validation.ts for client-side use.
 */

export const PASSWORD_RULES = {
  minLength: 8,
  maxLength: 128,
  requireLowercase: true,
  requireUppercase: true,
  requireDigit: true,
  requireSpecialChar: true,
}

export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (typeof password !== 'string') return { valid: false, error: 'Password must be a string' }
  if (password.length < 8) return { valid: false, error: 'Password must be at least 8 characters' }
  if (password.length > 128) return { valid: false, error: 'Password must be at most 128 characters' }
  if (!/[a-z]/.test(password)) return { valid: false, error: 'Password must contain at least one lowercase letter' }
  if (!/[A-Z]/.test(password)) return { valid: false, error: 'Password must contain at least one uppercase letter' }
  if (!/\d/.test(password)) return { valid: false, error: 'Password must contain at least one number' }
  if (!/[^a-zA-Z0-9]/.test(password)) return { valid: false, error: 'Password must contain at least one special character' }
  return { valid: true }
}

export function validateEmail(email: string): { valid: boolean; error?: string } {
  if (typeof email !== 'string') return { valid: false, error: 'Email must be a string' }
  const trimmed = email.trim().toLowerCase()
  if (!trimmed) return { valid: false, error: 'Email is required' }
  if (trimmed.length > 254) return { valid: false, error: 'Email is too long' }
  if (/[\s\n\r\t]/.test(trimmed)) return { valid: false, error: 'Email contains invalid characters' }
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) return { valid: false, error: 'Invalid email format' }
  return { valid: true }
}

export function sanitizeInput(input: string, maxLength = 256): string {
  if (typeof input !== 'string') return ''
  return input
    .replace(/<[^>]*>/g, '')
    .replace(/[<>"'&]/g, '')
    .trim()
    .slice(0, maxLength)
}

/**
 * The `redirect` query param on /login and /signup says where to send someone
 * once they're signed in, and it comes straight from whatever link they
 * followed — so it may only ever lead back into this site. It used to be
 * passed to router.push / window.location as-is, which let a crafted link use
 * our own sign-in page to forward a visitor to any outside address (or to a
 * `javascript:` URL). Browser-only: this one has no backend counterpart.
 *
 * The value is resolved with the URL parser rather than tested for a leading
 * "/", because that is what catches the look-alikes: "//host", "/\host", and a
 * tab or newline tucked between two slashes all parse as a different origin.
 * Returns a same-site path (with its query and hash), or null when the param
 * is missing or points anywhere else.
 */
export function getRedirectParam(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = new URLSearchParams(window.location.search).get('redirect')
    if (!raw) return null
    const origin = window.location.origin
    // Both pages have always decoded the value a second time, so check the
    // string they would actually have used. A malformed escape throws here
    // and lands in the catch below instead of crashing the page mid-render.
    const url = new URL(decodeURIComponent(raw), origin)
    if (url.origin !== origin) return null
    const path = `${url.pathname}${url.search}${url.hash}`
    // Normalising can leave a path that itself starts with "//" ("/.//host"
    // does), which a browser reads as another site — so the result gets the
    // same check as the input.
    if (new URL(path, origin).origin !== origin) return null
    return path
  } catch {
    return null
  }
}
